import { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useResource } from "../hooks/useResource";
import { ErrorBanner, LoadingState, Mono, PageHeader, when } from "../components/common";
import { MONO } from "../theme";
import { OutcomeChip } from "./OutcomeChip";
import { PortalOutcomeDialog } from "./PortalOutcomeDialog";
import {
  FALLBACK_OUTCOMES,
  type PortalEvidence,
  type PortalLeadDetail,
  type PortalSummary,
} from "./types";

/**
 * One lead, as the client sees it: why it is worth a call, who to call, whether it
 * reached them, and the box to say what happened.
 *
 * A lead belonging to another client is a 404 from the API — the same answer as an
 * id that does not exist — so this screen has no "not permitted" state to render
 * and should not invent one.
 */
export function PortalLeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const detail = useResource<PortalLeadDetail>(id ? `/api/portal/leads/${id}` : null);
  const summary = useResource<PortalSummary>("/api/portal/summary");
  const [reporting, setReporting] = useState(false);

  if (detail.loading && !detail.data) return <LoadingState label="Loading lead…" />;
  if (detail.error) return <ErrorBanner error={detail.error} onRetry={detail.reload} />;
  if (!detail.data) return null;

  const { lead, contacts, deliveries } = detail.data;
  // Annotated so the `{}` fallback is contextually typed rather than inferred as
  // an empty object type the field reads below would not typecheck against.
  const evidence: PortalEvidence = lead.payload?.event ?? {};
  const sourceUrl = typeof evidence.sourceUrl === "string" ? evidence.sourceUrl : null;
  const emails = contacts.filter((c) => c.kind === "email");
  const phones = contacts.filter((c) => c.kind === "phone");
  const others = contacts.filter((c) => c.kind !== "email" && c.kind !== "phone");
  const outcomes = summary.data?.outcomes ?? FALLBACK_OUTCOMES;
  const portfolio = lead.payload?.organization?.portfolioBuildings;

  return (
    <Box>
      <PageHeader
        title={lead.address ?? lead.org ?? lead.signalKey}
        subtitle={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip label={lead.signalLabel ?? lead.signalKey} variant="outlined" />
            {lead.market ? <Chip label={lead.market} variant="outlined" /> : null}
            <OutcomeChip outcome={lead.outcome} />
            <Typography variant="body2" color="text.secondary">
              Received {when(lead.assignedAt)}
            </Typography>
          </Stack>
        }
        actions={
          <>
            <Button component={RouterLink} to="/portal/leads" startIcon={<ArrowBackIcon />}>
              Back to leads
            </Button>
            <Button variant="contained" onClick={() => setReporting(true)}>
              {lead.outcome ? "Update outcome" : "Report outcome"}
            </Button>
          </>
        }
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2 }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Why now
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {lead.whyNow ?? lead.payload?.whyNow ?? lead.signalLabel ?? lead.signalKey}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.5 }}>
              <Field label="Event date" value={lead.eventAt ? lead.eventAt.slice(0, 10) : "—"} />
              <Field label="Age" value={lead.daysOld === null ? "undated" : `${lead.daysOld} days`} />
              <Field label="Property kind" value={lead.propertyKind ?? "—"} />
              <Field label="Units" value={lead.resUnits != null ? String(lead.resUnits) : "—"} />
              <Field label="Year built" value={lead.yearBuilt ?? "—"} />
              <Field
                label="Owner portfolio"
                value={portfolio != null ? `${portfolio} buildings` : "—"}
              />
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Official record
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableBody>
                {(
                  [
                    ["Source", evidence.source],
                    ["Reference", evidence.reference],
                    ["Status", evidence.status],
                    ["Severity", evidence.severity],
                    ["Description", evidence.description],
                    ["Deadline", evidence.deadline],
                    ["Balance due", evidence.balanceDue],
                  ] as const
                ).map(([label, raw]) => (
                  <TableRow key={label}>
                    <TableCell sx={{ width: 140, color: "text.secondary", border: 0 }}>{label}</TableCell>
                    <TableCell sx={{ border: 0 }}>
                      {raw === null || raw === undefined || raw === "" ? "—" : String(raw)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {sourceUrl ? (
              <Link href={sourceUrl} target="_blank" rel="noreferrer noopener" variant="body2">
                Open the public record
              </Link>
            ) : null}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Who to call
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              {lead.org ?? "No owner resolved"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lead.orgKind ?? "—"}
            </Typography>

            {contacts.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No contact details yet for this owner. They are filled in automatically as they are
                found — the lead itself is still real.
              </Alert>
            ) : (
              <Table size="small" sx={{ mt: 1.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Contact</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Confidence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...emails, ...phones, ...others].map((c) => (
                    <TableRow key={`${c.kind}-${c.value}`}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {c.kind === "email" || c.kind === "phone" ? (
                            <Link
                              href={c.kind === "email" ? `mailto:${c.value}` : `tel:${c.value}`}
                              variant="body2"
                              sx={{ fontFamily: MONO }}
                            >
                              {c.value}
                            </Link>
                          ) : (
                            <Mono>{c.value}</Mono>
                          )}
                          {/* The provider's own deliverability verdict, shown only when
                              it is not a clean "deliverable" — a warning is worth the
                              row, a green tick is noise. */}
                          {c.verification && c.verification !== "deliverable" ? (
                            <Tooltip title="How confident we are this address is reachable. Worth a check before you mail it.">
                              <Chip
                                label={c.verification}
                                color={c.verification === "undeliverable" ? "error" : "warning"}
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{c.title ?? "—"}</TableCell>
                      <TableCell>
                        {c.confidence != null ? `${Math.round(c.confidence * 100)}%` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Stack>

        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Your outcome
            </Typography>
            <Box sx={{ mt: 1 }}>
              <OutcomeChip outcome={lead.outcome} />
            </Box>
            {lead.outcomeReportedAt ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Reported {when(lead.outcomeReportedAt)}
              </Typography>
            ) : null}
            {lead.outcomeValueUsd != null ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Value ${lead.outcomeValueUsd.toLocaleString()}
              </Typography>
            ) : null}
            {lead.outcomeNote ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                {lead.outcomeNote}
              </Typography>
            ) : null}
            <Button sx={{ mt: 2 }} variant="contained" fullWidth onClick={() => setReporting(true)}>
              {lead.outcome ? "Update outcome" : "Report outcome"}
            </Button>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Delivery
            </Typography>
            {deliveries.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Not sent to a channel. It is here in the portal either way.
              </Typography>
            ) : (
              <Table size="small" sx={{ mt: 1 }}>
                <TableBody>
                  {deliveries.map((d, i) => (
                    <TableRow key={`${d.channelType}-${d.attempt}-${i}`}>
                      <TableCell sx={{ border: 0 }}>{d.channelType ?? "channel"}</TableCell>
                      <TableCell sx={{ border: 0 }}>
                        <Chip
                          label={d.status}
                          color={d.status === "ok" ? "success" : "error"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ border: 0, color: "text.secondary" }}>{when(d.at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Whether each attempt reached your channel. Ask your account contact if one keeps failing.
            </Typography>
          </Paper>
        </Stack>
      </Box>

      {reporting ? (
        <PortalOutcomeDialog
          lead={lead}
          outcomes={outcomes}
          onClose={() => setReporting(false)}
          onSaved={() => {
            setReporting(false);
            detail.reload();
            summary.reload();
          }}
        />
      ) : null}
    </Box>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
